import React, {
  MouseEventHandler,
  useEffect,
  useState,
  KeyboardEventHandler,
  useCallback
} from 'react';
import LinkWrapper from '@src/components/LinkWrapper';
import styles from './MegaMenuDesktop.module.scss';
import BoxIcon from '@src/elements/BoxIcon';
import classNames from 'classnames';
import { Maybe } from '@src/graphql/gql-types';
import { HyperLink } from '@src/types/contentful';
import { useHoverIntent } from '@src/hooks/useHoverIntent';
import {
  getAdjacentMegaMenuItems,
  groupMegaMenuItemsByContentType
} from '@src/utils/communityLocations';
import { camelizeString } from '@src/utils/strings';
import usePreventKeyboardScrolling from '@src/hooks/usePreventKeyboardPageScrolling';

interface SharedProps {
  parentItem?: HyperLink;
  communityNav?: HyperLink[];
  handleCloseMegaMenu?:
    | KeyboardEventHandler<HTMLAnchorElement>
    | MouseEventHandler<HTMLAnchorElement>;
}

interface MegaMenu extends SharedProps {
  isOpen: boolean;
}

interface MenuList extends SharedProps {
  type: 'state' | 'region' | 'area' | 'suburb';
  isListActive: boolean;
  isMegaMenuOpen: boolean;
  activeItems: Maybe<HyperLink[]>;
  selectedItem: Maybe<string>;
  onHoverOver?: (item: HyperLink) => void;
  isLastList?: boolean;
}
interface MenuLinkItem extends HyperLink, SharedProps {
  type: 'state' | 'region' | 'area' | 'suburb';
  isActive: boolean;
  isListActive: boolean;
  isMegaMenuOpen: boolean;
  isLastListItem?: boolean;
  onHoverOver?: () => void;
}

const MenuLinkItem: React.FC<MenuLinkItem> = ({
  type,
  communityNav,
  parentItem,
  handleCloseMegaMenu,
  title,
  urlPath,
  onHoverOver,
  isActive,
  isListActive,
  isLastListItem = false,
  isMegaMenuOpen
}) => {
  const isSunshineCoast = title === 'Sunshine Coast';
  const [isHovering, , ref] = useHoverIntent({
    timeout: 0,
    sensitivity: 10,
    interval: 100
  });
  const adjacentItems = getAdjacentMegaMenuItems(
    communityNav || [],
    title || '',
    isSunshineCoast,
    type
  );
  const topLevelItemRef = !adjacentItems?.above
    ? camelizeString(parentItem?.title || '')
    : adjacentItems?.above;

  useEffect(() => {
    if (isHovering && onHoverOver) {
      onHoverOver();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHovering]);

  const focusItem = useCallback(
    (e: React.KeyboardEvent<HTMLAnchorElement>) => {
      if (document?.readyState === 'complete') {
        if (e.key === 'ArrowUp') {
          if (!adjacentItems?.above && !!parentItem) {
            const parentRef = document?.getElementById(`${topLevelItemRef}`);
            parentRef?.focus();
          } else {
            const ref = document?.getElementById(`${adjacentItems?.above}`);
            ref?.focus();
          }
        }
        if (e.key === 'ArrowDown') {
          const ref = document?.getElementById(`${adjacentItems?.below}`);
          ref?.focus();
        }
        if (e.key === 'ArrowRight') {
          const ref = document?.getElementById(`${adjacentItems?.next}`);
          ref?.focus();
        }
        if (e.key === 'ArrowLeft') {
          const ref = document?.getElementById(`${adjacentItems?.previous}`);
          ref?.focus();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adjacentItems]
  );

  return (
    <li
      ref={ref as React.RefObject<HTMLLIElement>}
      className={classNames(styles.menuListItem, {
        [styles.menuListItemActive]: isActive,
        [styles.menuListItemMenuActive]: isListActive && isMegaMenuOpen,
        [styles.menuListItemCommunity]: isLastListItem
      })}
    >
      <LinkWrapper
        id={`${camelizeString(title || '')}${
          isSunshineCoast ? `-${type}` : ''
        }`}
        aria-label={title || ''}
        role="menuitem"
        href={urlPath || '/find-your-community'}
        openNewWindow={false}
        onClick={handleCloseMegaMenu as MouseEventHandler<HTMLAnchorElement>}
        onFocus={() => {
          onHoverOver?.();
        }}
        onKeyUp={(e) => focusItem(e)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            (handleCloseMegaMenu as KeyboardEventHandler<HTMLAnchorElement>)(e);
          }
          if (isLastListItem && (e.key === 'ArrowRight' || e.key === 'Tab')) {
            (handleCloseMegaMenu as KeyboardEventHandler<HTMLAnchorElement>)(e);
          }
        }}
      >
        {title}
        {!isLastListItem && <BoxIcon name="right-arrow-alt" />}
      </LinkWrapper>
    </li>
  );
};

const MenuList: React.FC<MenuList> = ({
  type,
  communityNav,
  parentItem,
  activeItems,
  handleCloseMegaMenu,
  onHoverOver,
  selectedItem,
  isListActive,
  isLastList = false,
  isMegaMenuOpen
}) => (
  <ul
    className={classNames(styles.menuList, {
      [styles.menuListActive]: isListActive
    })}
  >
    {activeItems?.map((item, index) => (
      <MenuLinkItem
        key={`${item?.title}-${index}`}
        {...item}
        type={type}
        parentItem={parentItem}
        communityNav={communityNav}
        handleCloseMegaMenu={handleCloseMegaMenu}
        onHoverOver={() => onHoverOver?.(item)}
        isListActive={isListActive}
        isActive={selectedItem === item?.title}
        isLastListItem={isLastList}
        isMegaMenuOpen={isMegaMenuOpen}
      />
    ))}
  </ul>
);

const MegaMenuDesktop: React.FC<MegaMenu> = ({
  isOpen,
  parentItem,
  communityNav,
  handleCloseMegaMenu
}) => {
  const [selectedState, setSelectedState] = useState<Maybe<string>>(null);
  const [selectedRegion, setSelectedRegion] = useState<Maybe<string>>(null);
  const [selectedArea, setSelectedArea] = useState<Maybe<string>>(null);
  const [regions, setRegions] = useState<Maybe<HyperLink[]>>(null);
  const [areas, setAreas] = useState<Maybe<HyperLink[]>>(null);
  const [suburbs, setSuburbs] = useState<Maybe<HyperLink[]>>(null);
  const renderAllForA11y = groupMegaMenuItemsByContentType(communityNav || []);

  // Reset when menu closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedState(null);
      setSelectedRegion(null);
      setSelectedArea(null);
      setRegions(null);
      setAreas(null);
      setSuburbs(null);
    }
  }, [isOpen]);

  // Disable arrow key page scrolling when menu is open
  usePreventKeyboardScrolling(isOpen);

  return (
    <div className={styles.megaMenu} data-testid="desktopMegaMenu">
      {/* STATE PANEL */}
      <MenuList
        type="state"
        parentItem={parentItem}
        isListActive={true}
        activeItems={communityNav}
        handleCloseMegaMenu={handleCloseMegaMenu}
        onHoverOver={(stateItem) => {
          setRegions(stateItem?.subNav);
          setAreas(null);
          setSuburbs(null);
          setSelectedState(stateItem?.title);
          setSelectedRegion(null);
          setSelectedArea(null);
        }}
        selectedItem={selectedState}
        communityNav={communityNav}
        isMegaMenuOpen={isOpen}
      />

      {/* REGION PANEL */}
      <MenuList
        type="region"
        isListActive={!!regions}
        activeItems={regions || renderAllForA11y.regions}
        handleCloseMegaMenu={handleCloseMegaMenu}
        onHoverOver={(regionItem) => {
          setAreas(regionItem?.subNav);
          setSuburbs(null);
          setSelectedRegion(regionItem?.title);
          setSelectedArea(null);
        }}
        selectedItem={selectedRegion}
        communityNav={communityNav}
        isMegaMenuOpen={isOpen}
      />

      {/* AREA PANEL */}
      <MenuList
        type="area"
        isListActive={!!areas}
        activeItems={areas || renderAllForA11y.areas}
        handleCloseMegaMenu={handleCloseMegaMenu}
        onHoverOver={(areaItem) => {
          setSuburbs(areaItem?.subNav);
          setSelectedArea(areaItem?.title);
        }}
        selectedItem={selectedArea}
        communityNav={communityNav}
        isMegaMenuOpen={isOpen}
      />

      {/* SUBURB PANEL */}
      <MenuList
        type="suburb"
        isListActive={!!suburbs}
        activeItems={suburbs || renderAllForA11y.suburbs}
        handleCloseMegaMenu={handleCloseMegaMenu}
        selectedItem={undefined}
        communityNav={communityNav}
        isLastList
        isMegaMenuOpen={isOpen}
      />
    </div>
  );
};

export default MegaMenuDesktop;
